import { z } from 'zod';

export const PropertySchema = z.object({
  name: z.string(),
  type: z.string(),
  required: z.boolean().optional(),
  description: z.string(),
  missing_when: z.string().optional(),
  allowed_values: z.array(z.string()).optional(),
  items: z.object({ type: z.string() }).optional(),
});

const PropertyGroupSchema = z.object({
  label: z.string(),
  properties: z.array(PropertySchema),
});

export const LocationSchema = z.object({
  file: z.string(),
  line: z.number(),
  context: z.string().optional(),
  github_url: z.string().optional(),
});

const EventSchema = z.object({
  id: z.string(),
  name: z.string(),
  event_type: z.enum(['track', 'identify', 'page', 'group', 'screen']),
  description: z.string(),
  when_fired: z.string().optional(),
  frequency: z.string().optional(),
  notes: z.array(z.string()).optional(),
  property_groups: z.array(PropertyGroupSchema).optional(),
  properties: z.array(PropertySchema).optional(),
  locations: z.array(LocationSchema).optional(),
  tracking_plan: z
    .object({
      name: z.string(),
      status: z.enum(['validated', 'violation', 'unplanned']),
      violations_30d: z.number().optional(),
    })
    .optional(),
});

const TraitSchema = z.object({
  name: z.string(),
  type: z.string(),
  description: z.string(),
  missing_when: z.string().optional(),
  allowed_values: z.array(z.string()).optional(),
});

const IdentifySchema = z.object({
  when_fired: z.string().optional(),
  user_id_source: z.string().optional(),
  locations: z.array(LocationSchema).optional(),
  traits: z.array(TraitSchema),
});

const GroupSchema = z.object({
  when_fired: z.string().optional(),
  group_id_source: z.string().optional(),
  locations: z.array(LocationSchema).optional(),
  traits: z.array(TraitSchema),
});

export const SourceDocSpecSchema = z.object({
  version: z.string(),
  kind: z.literal('source_doc'),
  application: z
    .object({
      name: z.string(),
      description: z.string(),
      sdk_type: z.string().optional(),
      sdk_version: z.string().optional(),
      analytics_infrastructure: z
        .array(
          z.object({
            file: z.string(),
            purpose: z.string(),
          }),
        )
        .optional(),
    })
    .optional(),
  events: z.array(EventSchema).default([]),
  common_properties: z
    .array(
      z.object({
        name: z.string(),
        type: z.string().optional(),
        required: z.boolean().optional(),
        description: z.string().optional(),
        source: z.string().optional(),
        missing_when: z.string().optional(),
      }),
    )
    .optional(),
  identify: IdentifySchema.optional(),
  group: GroupSchema.optional(),
  page_calls: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        description: z.string().optional(),
        when_fired: z.string().optional(),
        properties: z.array(PropertySchema).optional(),
        locations: z.array(LocationSchema).optional(),
      }),
    )
    .optional(),
  connected_destinations: z
    .array(
      z.object({
        id: z.string(),
        name: z.string(),
        type: z.string(),
        transformations: z
          .array(
            z.object({
              id: z.string(),
              name: z.string(),
            }),
          )
          .optional(),
      }),
    )
    .optional(),
  metadata: z
    .object({
      source_id: z.string(),
      repo: z.string().optional(),
      branch: z.string().optional(),
      commit_sha: z.string().optional(),
      generated_at: z.string(),
      agent_model: z.string().optional(),
    })
    .optional(),
});

export type SourceDocSpec = z.infer<typeof SourceDocSpecSchema>;

export function validateDocSpec(
  spec: unknown,
): { valid: true; data: SourceDocSpec } | { valid: false; errors: string[] } {
  const result = SourceDocSpecSchema.safeParse(spec);
  if (result.success) {
    return { valid: true, data: result.data };
  }
  return {
    valid: false,
    errors: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
  };
}

// ─── Measurement Plan Schema ───

const PropertyChangeSchema = z.object({
  name: z.string(),
  type: z.string(),
  required: z.boolean().optional(),
  description: z.string(),
  status: z.enum(['added', 'modified', 'removed']),
  previous_type: z.string().optional(),
  previous_required: z.boolean().optional(),
  missing_when: z.string().optional(),
  allowed_values: z.array(z.string()).optional(),
  // Agents sometimes emit numeric or boolean example literals (e.g. 42, true).
  // Coerce any primitive to string at the parse boundary so the renderer
  // always sees a string.
  example: z
    .union([z.string(), z.number(), z.boolean()])
    .transform((v) => String(v))
    .optional(),
  items: z.object({ type: z.string() }).optional(),
});

const PlanLocationSchema = LocationSchema.omit({ github_url: true }).extend({
  permalink_url: z.string().optional(),
  repo: z.string(),
});

const MeasurementPlanEventSchema = z.object({
  id: z.string(),
  name: z.string(),
  event_type: z.literal('track'),
  change_type: z.enum(['add', 'modify', 'remove', 'already_available']),
  description: z.string(),
  when_fired: z.string().optional(),
  existing_reference: z.string().optional(),
  source_ids: z.array(z.string()),
  properties: z.array(PropertyChangeSchema).optional(),
  locations: z.array(PlanLocationSchema).optional(),
  tracking_plan: z
    .object({
      name: z.string(),
      status: z.enum(['validated', 'violation', 'unplanned']),
      violations_30d: z.number().optional(),
    })
    .optional(),
});

const IdentifyChangeSchema = z.object({
  id: z.string(),
  change_type: z.enum(['add', 'modify', 'remove']),
  description: z.string(),
  source_ids: z.array(z.string()),
  traits: z.array(PropertyChangeSchema).optional(),
  locations: z.array(PlanLocationSchema).optional(),
});

const GroupChangeSchema = z.object({
  id: z.string(),
  change_type: z.enum(['add', 'modify', 'remove']),
  description: z.string(),
  source_ids: z.array(z.string()),
  traits: z.array(PropertyChangeSchema).optional(),
  locations: z.array(PlanLocationSchema).optional(),
});

const PageChangeSchema = z.object({
  id: z.string(),
  name: z.string(),
  call_type: z.enum(['page', 'screen']),
  change_type: z.enum(['add', 'modify', 'remove']),
  description: z.string(),
  category: z.string().optional(),
  source_ids: z.array(z.string()),
  properties: z.array(PropertyChangeSchema).optional(),
  locations: z.array(PlanLocationSchema).optional(),
});

export const MeasurementPlanSpecSchema = z
  .object({
    version: z.literal('rudder-ai/measurement-plan/v0.1'),
    kind: z.literal('measurement_plan'),
    sources: z
      .array(
        z.object({
          source_id: z.string(),
          name: z.string(),
          type: z.string(),
        }),
      )
      .max(50),
    events: z.array(MeasurementPlanEventSchema).max(100),
    identify_changes: z.array(IdentifyChangeSchema).max(50).optional(),
    group_changes: z.array(GroupChangeSchema).max(50).optional(),
    page_changes: z.array(PageChangeSchema).max(50).optional(),
    common_properties: z.array(PropertyChangeSchema).max(100).optional(),
    metadata: z.object({
      generated_at: z.string(),
      agent_model: z.string(),
      baseline_doc_ids: z.array(z.string()).optional(),
    }),
  })
  .superRefine((spec, ctx) => {
    for (const [i, event] of spec.events.entries()) {
      if ((event.change_type === 'add' || event.change_type === 'modify') && (event.locations?.length ?? 0) === 0) {
        ctx.addIssue({
          code: 'custom',
          message: 'locations[] is required for add/modify events',
          path: ['events', i, 'locations'],
        });
      }
    }
  });

export type MeasurementPlanSpecSchemaType = z.infer<typeof MeasurementPlanSpecSchema>;

export function validateMeasurementPlanSpec(
  spec: unknown,
): { valid: true; data: MeasurementPlanSpecSchemaType } | { valid: false; errors: string[] } {
  const result = MeasurementPlanSpecSchema.safeParse(spec);
  if (result.success) {
    return { valid: true, data: result.data };
  }
  return {
    valid: false,
    errors: result.error.issues.map((i) => `${i.path.join('.')}: ${i.message}`),
  };
}

/**
 * Looser preview shape for the chat artifact renderer.
 *
 * Agents emit `measurement_plan` UI messages as a draft *before* the full
 * spec is finalized — in particular, before `sources[].type` and the
 * `metadata.generated_at` / `metadata.agent_model` fields are filled in by
 * the backend at save time. The chat renderer shouldn't crash on those
 * unfilled fields; `save_measurement_plan` still uses the strict
 * MeasurementPlanSpecSchema above, so the DB shape stays authoritative.
 */
const MeasurementPlanSpecPreviewSchema = z.object({
  version: z.literal('rudder-ai/measurement-plan/v0.1'),
  kind: z.literal('measurement_plan'),
  sources: z.array(
    z.object({
      source_id: z.string(),
      // All of name/type are draft-time — the agent may omit either in the
      // first artifact before Step 0 discovery has fully filled them in. The
      // strict schema on save_measurement_plan still enforces name; this is
      // just the chat renderer being forgiving.
      name: z.string().optional(),
      type: z.string().optional(),
    }),
  ),
  events: z.array(MeasurementPlanEventSchema),
  identify_changes: z.array(IdentifyChangeSchema).optional(),
  group_changes: z.array(GroupChangeSchema).optional(),
  page_changes: z.array(PageChangeSchema).optional(),
  common_properties: z.array(PropertyChangeSchema).optional(),
  metadata: z
    .object({
      generated_at: z.string().optional(),
      agent_model: z.string().optional(),
      baseline_doc_ids: z.array(z.string()).optional(),
    })
    .optional(),
});

/** Envelope shape emitted by the agent via `mcp__ui__send_to_ui` with type: "measurement_plan". */
export const MeasurementPlanChatPayloadSchema = z.object({
  spec: MeasurementPlanSpecPreviewSchema,
  planId: z
    .string()
    .regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i, 'planId must be a UUID')
    .nullable(),
  isSaved: z.boolean(),
  title: z.string(),
  description: z.string().optional(),
  businessContext: z.string().optional(),
  summary: z.object({
    added: z.number().int().nonnegative(),
    modified: z.number().int().nonnegative(),
    removed: z.number().int().nonnegative(),
    alreadyAvailable: z.number().int().nonnegative(),
  }),
  warnings: z.array(z.string()),
});
